import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import 'moment-timezone';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Image from 'react-bootstrap/Image';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import Dropdown from 'react-bootstrap/Dropdown';
import CreateIcon from '@material-ui/icons/Create';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { isDefined } from '../../utils/utils';

//CSS, STYLES & MEDIA ASSETS
import PatientIcon from '../../utils/account-circle.svg';
import './PatientsGrid.scss';
import { parse } from 'date-fns';

//Scss variable

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc'
        ? (a, b) => desc(a, b, orderBy)
        : (a, b) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props) {
    const { classes, order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    const { t } = useTranslation();
    const headCells = [
        { id: 'userMenu', numeric: false, disablePadding: true, label: '' },
        { id: 'Name', numeric: false, disablePadding: false, label: t('WoundsGrid.Name') },
        { id: 'Type', numeric: false, disablePadding: false, label: t('WoundsGrid.Type') },
        { id: 'Identification', numeric: false, disablePadding: false, label: t('WoundsGrid.SNS') },
        { id: 'Room', numeric: false, disablePadding: false, label: t('WoundsGrid.Room') },
        { id: 'Bed', numeric: false, disablePadding: false, label: t('WoundsGrid.Bed') }
    ];

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order.toLowerCase() : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id} //apenas apresenta a seta no campo que está aplicada a ordenação
                            direction={order.toLowerCase()}
                            className={(classes.label, classes.icon)}
                            icon
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired
};

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',

        marginTop: theme.spacing(1)
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2)
    },
    table: {
        minWidth: 750
    },
    tableWrapper: {
        overflowX: 'auto'
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1
    },
    label: {
        fontSize: 12
    },
    fontSizeGrid: {
        fontSize: 12
    },
    icon: {
        fontSize: 13,
        fontWeight: 700
    },
}));

const useStylesTooltip = makeStyles((theme) => ({
    tooltip: {
        maxWidth: 500, //'none'
        //backgroundColor: theme.palette.common.white,
        font: 'Nunito',
        color: '#494949',
        backgroundColor: 'white',
        minWidth: 150,
        border: 'solid',
        borderWidth: 1,
        borderColor: theme.palette.primary.main,
        fontSize: 12,
        margin: theme.spacing(1),
        marginLeft: 15
        /* boxShadow: theme.shadows[1], */
    },
    arrow: {
        color: theme.palette.primary.main,
        top: 25
    },
    tooltipArrow: {
        marginRight: -125
    }
}));

export default function EnhancedTable(props) {
    const { t } = useTranslation();
    const listPatients = useSelector((state) => state.patientsManage.listPatients) || [];
    const store = useSelector((store) => store);

    const classes = useStyles();
    const [order, setOrder] = React.useState(store.patientsManage.Order.toLowerCase()); //React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(store.patientsManage.PropertyOrder); //React.useState('nextIntervention');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(store.patientsManage.Page); //React.useState(0);
    const [dense, setDense] = React.useState(true);
    //const [rowsPerPage, setRowsPerPage] = React.useState(store.patientsManage.NumberRowsPerPage); //React.useState(5);

    const classesTooltip = useStylesTooltip();

    const handleRequestSort = (event, property) => {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);

        store.patientsManage.Refresh = true;
        store.patientsManage.Order = isDesc ? 'ASC' : 'DESC';
        store.patientsManage.PropertyOrder = property;
        props.changeOrder();
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = listPatients.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);

        store.patientsManage.Refresh = true;
        store.patientsManage.Page = newPage;
        props.changeOrder();
    };

    const handleChangeRowsPerPage = (event) => {
        //setRowsPerPage(parseInt(event.target.value, 25));
        //setPage(0);

        store.patientsManage.Refresh = true;
        store.patientsManage.NumberRowsPerPage = event.target.value;
        store.patientsManage.Page = 0;
        props.changeOrder();
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    // A lista de utentes só tem os registos da página
    //const emptyRows = rowsPerPage - Math.min(rowsPerPage, listPatients.length - page * rowsPerPage);
    //const emptyRows = rowsPerPage - Math.min(rowsPerPage, listPatients.length);
    const emptyRows = listPatients.length === 0 ? 1 : 0; // decidido com GN a grelha ocupar só o espaço das linhas ocupadas.
    //const emptyRows = Math.min(rowsPerPage, listPatients.length - page * rowsPerPage) === 1 ? 1 : 0;

    const selectPatient = (patientId) => (event) => {
        event.stopPropagation();
        if (patientId !== 0) {
            store.patientsManage.PatientID = patientId;

            props.selectPatient();
        }
    };

    const deletePatient = (patientId) => (event) => {
        event.stopPropagation();
        if (patientId !== 0) {
            props.deletePatient(patientId);
        }
    };

    const CustomToggleMenu = React.forwardRef(({ children, onClick }, ref) => (
        <a
            className="text-decoration-none font-weight-bold d-block"
            ref={ref}
            href="/"
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onClick(e);
            }}
        >
            <IconButton
                className="outline-none button-grid-padding"
                centerRipple={true}
            >
                <MoreVertIcon />
            </IconButton>
        </a>
    ));

    const getAtualInternment = (listInternement) => {
        let newList = listInternement;

        let internment = null;
        newList.sort((a, b) => (a.exitDate != null && b.exitDate == null ? 1 :
            a.exitDate == null && b.exitDate != null ? -1 :
                (a.exitDate != null && b.exitDate != null ? (a.exitDate < b.exitDate ? 1 : b.exitDate < a.exitDate ? -1 : 0) :
                    (a.entrydate < b.entrydate ? 1 : b.entrydate < a.entrydate ? -1 : 0))
        ));

        if (newList[0].exitDate == null) {
            internment = newList[0];
        }

        return internment;

    }

    return (
        <>
            <div className={classes.root} /* hidden={listPatients.length === 0 || props.isLoading} */ >
                <Paper className={classes.paper}>
                    <div className={classes.tableWrapper}>
                        <Table
                            className={classes.table}
                            aria-labelledby='tableTitle'
                            size={dense ? 'small' : 'medium'}
                            aria-label='enhanced table'
                        >
                            <EnhancedTableHead
                                classes={classes}
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={store.patientsManage.NumberOfRecords} //{listPatients.length}
                            />
                            <TableBody>
                                {stableSort(listPatients, getSorting(order, orderBy))
                                    // A paginação é efetuada pelo serviço. A cada alteração de página é efetuado um novo pedido, logo não fazemos nada na grelha a não ser refrescar
                                    //.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {
                                        const isItemSelected = isSelected(row.name);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        const actualInternment = isDefined(row.internment) && row.internment.length > 0 ? getAtualInternment(row.internment) : null;

                                        return (
                                            <TableRow
                                                hover
                                                //onClick={event => handleClick(event, row.name)}
                                                classes={classes.MuiTableRow}
                                                onClick={selectPatient(row.id)}
                                                role='checkbox'
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.lastInterventionWoundId}
                                                selected={isItemSelected}
                                            >
                                                <TableCell className="p-2 table-cell-custom-size" align="left" >
                                                    <Dropdown>
                                                        <Dropdown.Toggle as={CustomToggleMenu} ></Dropdown.Toggle>
                                                        <Dropdown.Menu className="py-3 dropdown-menu-user-grid ">
                                                            <Dropdown.Item as="button" className="institution-item nunito-regular font-size-16-px my-auto py-2 outline-none" onClick={selectPatient(row.id)}>
                                                                <CreateIcon className="custom-icon-font-size" />
                                                                <span className="ml-3 pt-2">{t('EditPatient')}</span>
                                                            </Dropdown.Item>
                                                            <Dropdown.Item as="button" className="institution-item nunito-regular font-size-16-px my-auto py-2 outline-none"
                                                                onClick={deletePatient(row.id)} disabled={row.hasWounds}
                                                            >
                                                                <DeleteIcon className="custom-icon-font-size" />
                                                                <span className="ml-3 pt-2">{t('DeletePatient')}</span>
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </TableCell>
                                                <Tooltip
                                                    disableFocusListener
                                                    title={row.name}
                                                    classes={classesTooltip}
                                                    arrow
                                                    placement='bottom-start'
                                                    enterDelay={300}
                                                >
                                                    <TableCell
                                                        component='td'
                                                        id={labelId}
                                                        scope='row'
                                                        className='name-width'
                                                    >
                                                        <Box className='d-flex'>
                                                            <Image
                                                                className={
                                                                    'text-left mr-3 my-auto flex-shrink-0 fit-photo' +
                                                                    (row.photoURI === '' || row.photoURI === null
                                                                        ? ''
                                                                        : ' border-img-grd')
                                                                }
                                                                width='30'
                                                                height='30'
                                                                roundedCircle
                                                                src={
                                                                    row.photoURI === '' || row.photoURI === null
                                                                        ? PatientIcon
                                                                        : row.photoURI
                                                                }
                                                                onError={(event) => {
                                                                    event.target.src = PatientIcon;
                                                                    event.target.className =
                                                                        'text-left mr-3 my-auto flex-shrink-0';
                                                                }}
                                                            ></Image>
                                                            <Typography
                                                                noWrap
                                                                className='my-auto font-weight-bold'
                                                                classes={{ root: classes.fontSizeGrid }}
                                                            >
                                                                {row.name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </Tooltip>
                                                <TableCell
                                                    scope='row'
                                                    align='left'
                                                    component='td'
                                                    className='type-width'
                                                >
                                                    {isDefined(row.identificationType) ? row.identificationType.type : ''}
                                                </TableCell>
                                                <TableCell align='left' className='id-width'>
                                                    {row.identification}
                                                </TableCell>
                                                <TableCell align='left' className='room-width'>
                                                    {/* {row.internment.length >= 1 &&
                                                        row.internment[0].exitDate == null &&
                                                        row.internment[0].room} */}

                                                    {isDefined(actualInternment) ? actualInternment.room : ''}
                                                </TableCell>
                                                <TableCell align='left' className='bed-width'>
                                                    {/* {row.internment.length >= 1 &&
                                                        row.internment[0].exitDate == null &&
                                                        row.internment[0].bed} */}
                                                    {isDefined(actualInternment) ? actualInternment.bed : ''}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 53 * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div>
                        <TablePagination
                            rowsPerPageOptions={[25, 50, 100]}
                            component='div'
                            count={store.patientsManage.NumberOfRecords} //{listPatients.length}
                            rowsPerPage={store.patientsManage.NumberRowsPerPage}
                            page={store.patientsManage.Page}
                            backIconButtonProps={{
                                'aria-label': t('previousPage'),
                                title: t('previousPage'),
                                style: { outline: 'none' }
                            }}
                            nextIconButtonProps={{
                                'aria-label': t('nextPage'),
                                title: t('nextPage'),
                                style: { outline: 'none' }
                            }}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            labelRowsPerPage={t('RowsPerPage')}
                        />
                    </div>
                </Paper>
            </div>
        </>
    );
}
